/**
 * Document requirements for the LIHTC affordable Upload Center.
 *
 * Source of truth for the mapping: "if the applicant said Yes to X,
 * these documents are required". Derived sections are rendered by
 * UploadCenter.tsx; the actual upload/verification is handled by the
 * site team downstream.
 *
 * Icon + i18n keys only — no React/DOM imports — so this can be
 * imported by any layer without pulling a tree.
 */

import type { HouseholdIncomeForm } from "./steps/HouseholdIncome";
import type { AssetsForm } from "./steps/Assets";
import type { ApplicationData } from "./applicationState";
import { configs } from "../../../shared/config/config";

interface MemberLike {
  relationship?: string;
  firstName?: string;
  lastName?: string;
}
interface MemberProfilesLike {
  firstName?: string;
  lastName?: string;
  members?: MemberLike[];
}
interface StudentDetailsLike {
  householdStatus?: string;
}

export interface DocumentSpec {
  /**
   * Unique key for localStorage bookkeeping. For per-person docs it includes
   * a suffix (e.g. `photoId:primary`, `photoId:member-0`) so each person's
   * upload is tracked separately.
   */
  key: string;
  /**
   * Base key used for i18n lookup (e.g. `photoId`). If unset, `key` is used.
   * Lets per-person specs share the same title/description template.
   */
  baseKey?: string;
  /**
   * Subject name to append to the row title, e.g. "Jane Doe" or the sentinel
   * "_primary_" which i18n renders as "You" / "Usted".
   */
  subject?: string;
  /**
   * Minimum number of files required to satisfy the slot. Required specs
   * use `count: 1`; the row is satisfied as soon as one file is uploaded.
   * Optional specs (`optional: true`) implicitly use min 0 — the resident
   * can leave the slot empty.
   */
  count: number;
  /**
   * Optional hard upload cap for the slot. Defaults to 6 in the UploadCenter
   * so residents can upload one combined PDF or several separate files
   * (e.g. six months of bank statements) without getting flooded with slots.
   * Override per-spec when a strict cap is required (e.g. `maxFiles: 1` for
   * a single ID photo).
   */
  maxFiles?: number;
  /** If true the row is a fallback/optional upload — renders an "Optional" chip instead of counting toward required completion. */
  optional?: boolean;
  /** If true the row renders a "Download template" link. */
  hasTemplate?: boolean;
  /** When `hasTemplate` is true, the URL the link points to. Typically a
   * static PDF served from `/forms/...` or an S3 URL. */
  templateUrl?: string;
  /** Accept attribute for the file input. */
  accept: string;
  /** Max file size in MB. */
  maxSizeMb: number;
}

export type SectionKey = "standard" | "income" | "asset";

export interface DocumentSection {
  /** Stable key mapped to i18n `uploadCenter.sections.<key>.title`. */
  sectionKey: SectionKey;
  /** Sub-grouping key (e.g. "employmentIncome" for income sources,
   * "standard" for the always-required block). Mapped to
   * `uploadCenter.groups.<groupKey>.title` for the group heading. */
  groupKey: string;
  documents: DocumentSpec[];
}

const ACCEPT = "application/pdf,image/jpeg,image/png";
const MAX_MB = 10;

/* ──────────────────────────────────────────────────────────────────────────
 * INCOME — trigger → required documents
 * ────────────────────────────────────────────────────────────────────────── */

type IncomeKey = keyof Omit<
  HouseholdIncomeForm,
  "otherIncomeDescription" | "additionalJobs"
>;

const INCOME_DOCS: Partial<Record<IncomeKey, DocumentSpec[]>> = {
  employmentIncome: [
    { key: "payStubs", count: 1, accept: ACCEPT, maxSizeMb: MAX_MB },
    {
      // Reviewer ask: collect a VOE or offer letter alongside paystubs.
      // Single required slot accepts either — keeps the second piece of
      // documentation captured without splitting into two slots when only
      // one applies to the applicant's situation.
      key: "employmentVerificationLetter",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
      hasTemplate: true,
      templateUrl: "/forms/employment-verification.pdf",
    },
  ],
  selfEmployment: [
    { key: "federalTaxReturn", count: 1, accept: ACCEPT, maxSizeMb: MAX_MB },
    { key: "profitAndLoss", count: 1, accept: ACCEPT, maxSizeMb: MAX_MB },
  ],
  unemployment: [
    {
      key: "unemploymentBenefitStatement",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
  ],
  vaGiBill: [
    {
      key: "vaBenefitAwardLetter",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
  ],
  militaryIncome: [{ key: "les", count: 1, accept: ACCEPT, maxSizeMb: MAX_MB }],
  publicAssistance: [
    {
      key: "publicAssistanceVerification",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
  ],
  ssRetirement: [
    { key: "ssBenefitLetter", count: 1, accept: ACCEPT, maxSizeMb: MAX_MB },
  ],
  ssiSsdi: [
    {
      key: "ssaBenefitAwardLetter",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
  ],
  disabilityDeathBenefits: [
    {
      key: "disabilityDeathBenefitLetter",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
  ],
  workersComp: [
    {
      key: "workersCompBenefitLetter",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
  ],
  // childSupport and alimonySpousalSupport upload sections used to live here
  // as simple Yes/No income-type triggers. Their drivers are now spousal-
  // status / cert fields (awardedIncome, childSupportStatus); the sections
  // are surfaced explicitly below.
  // cashAssistanceFromOthers + billPaymentHelp used to require a pre-printed
  // "recurringContributionAffidavit" upload. Now covered by the digitally
  // signed `recurring-gift-verification` cert (third-party source signs).
  trustsAnnuitiesPensions: [
    {
      key: "annuityPensionStatement",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
    { key: "trustDocument", count: 1, accept: ACCEPT, maxSizeMb: MAX_MB },
  ],
  realEstatePropertyIncome: [
    { key: "leaseAgreement", count: 1, accept: ACCEPT, maxSizeMb: MAX_MB },
    { key: "scheduleE", count: 1, accept: ACCEPT, maxSizeMb: MAX_MB },
  ],
  onlineIncome: [
    { key: "form1099", count: 1, accept: ACCEPT, maxSizeMb: MAX_MB },
    {
      key: "platformEarningsStatement",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
  ],
  educationAssistance: [
    {
      key: "educationAwardLetter",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
  ],
  unearnedIncomeMinors: [
    { key: "minorTrustStatement", count: 1, accept: ACCEPT, maxSizeMb: MAX_MB },
  ],
  publicHousingSection8: [
    {
      key: "housingAuthorityLetter",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
  ],
  // zeroIncomeMember used to require pre-printed `zeroIncomeCertification`
  // + `unemployedStatusAffidavit` uploads. Both are now signed digitally via
  // the `cert-of-zero-income` and `non-employed-cert` DocuSeal templates.
  otherIncomeNotListed: [
    {
      key: "otherIncomeDocumentation",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
  ],
};

// Cert-driven upload sections (not surfaced via INCOME_DOCS). Drivers live
// in Spousal Status sub-step / Child Support cert sub-step, not in the
// generic income Yes/No toggles.
//
// Child Support attachments are status-specific:
//   (a) Court-ordered, currently receiving           -> OAG Printout
//   (b) Court-ordered, not receiving / not anticipating -> OAG Printout
//   (c) Not court-ordered, receives cash             -> Cash Contribution Verification
//   (d) Anticipating, no court order                 -> Attorney Letter
//   (e) Never received / not anticipating            -> nothing
const CHILD_SUPPORT_OAG_DOC: DocumentSpec = {
  key: "childSupportOagPrintout",
  count: 1,
  accept: ACCEPT,
  maxSizeMb: MAX_MB,
};
const CHILD_SUPPORT_CASH_DOC: DocumentSpec = {
  key: "childSupportCashContributionVerification",
  count: 1,
  accept: ACCEPT,
  maxSizeMb: MAX_MB,
};
const CHILD_SUPPORT_ATTORNEY_DOC: DocumentSpec = {
  key: "childSupportAttorneyLetter",
  count: 1,
  accept: ACCEPT,
  maxSizeMb: MAX_MB,
};
const ALIMONY_DOCS: DocumentSpec[] = [
  { key: "alimonyDecree", count: 1, accept: ACCEPT, maxSizeMb: MAX_MB },
  {
    key: "alimonyPaymentHistory",
    count: 1,
    accept: ACCEPT,
    maxSizeMb: MAX_MB,
  },
];

/* ──────────────────────────────────────────────────────────────────────────
 * ASSET — trigger → required documents
 * ────────────────────────────────────────────────────────────────────────── */

const ASSET_DOCS: Partial<Record<keyof AssetsForm, DocumentSpec[]>> = {
  checking: [
    { key: "checkingStatements", count: 1, accept: ACCEPT, maxSizeMb: MAX_MB },
  ],
  savings: [
    { key: "savingsStatement", count: 1, accept: ACCEPT, maxSizeMb: MAX_MB },
  ],
  internetBased: [
    {
      key: "internetAssetStatement",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
  ],
  debitCards: [
    {
      key: "debitCardValueVerification",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
  ],
  moneyMarket: [
    {
      key: "moneyMarketStatement",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
  ],
  brokerage: [
    { key: "brokerageStatement", count: 1, accept: ACCEPT, maxSizeMb: MAX_MB },
  ],
  capitalInvestments: [
    {
      key: "capitalInvestmentOwnership",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
    {
      key: "capitalInvestmentValuation",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
  ],
  stocks: [
    {
      key: "stocksStatement",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
  ],
  bonds: [
    {
      key: "bondsStatement",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
  ],
  annuities: [
    {
      key: "annuityContractOrStatement",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
  ],
  cds: [{ key: "cdStatement", count: 1, accept: ACCEPT, maxSizeMb: MAX_MB }],
  cryptocurrency: [
    {
      key: "cryptoBalanceScreenshot",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
  ],
  lifeInsurance: [
    {
      key: "lifeInsuranceCashValue",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
  ],
  trustFunds: [
    { key: "trustAgreement", count: 1, accept: ACCEPT, maxSizeMb: MAX_MB },
    {
      key: "trustAccountStatement",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
  ],
  lumpSumAmounts: [
    {
      key: "lumpSumSourceDocumentation",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
  ],
  safetyDepositBox: [
    {
      key: "safetyDepositContentsList",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
    {
      key: "safetyDepositBankVerification",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
  ],
  realEstateProperty: [
    {
      key: "realEstateTaxAppraisal",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
    { key: "mortgageStatement", count: 1, accept: ACCEPT, maxSizeMb: MAX_MB },
  ],
  landContracts: [
    {
      key: "landContractDocument",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
    {
      key: "landContractPaymentLedger",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
  ],
  taxRefund: [
    {
      key: "taxRefundTranscript",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
  ],
  crowdsourcing: [
    {
      key: "crowdsourcingPayoutStatement",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
  ],
  // cashOnHand is self-declared — no document required from the resident.
  // Property staff verify in person at move-in; ProofUp will not extract.
  cashOnHand: [],
  personalPropertyInvestment: [
    {
      key: "personalPropertyAppraisal",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
  ],
  // assetsGivenAway used to require a "Bill of Sale / Gift Documentation"
  // upload. Disposal details (FMV, amount received, date) are now captured
  // in the application data + sworn under the `disposed-of-asset-affidavit`
  // DocuSeal cert.
  otherAssetsNotListed: [
    {
      key: "otherAssetDocumentation",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    },
  ],
};

/* ──────────────────────────────────────────────────────────────────────────
 * Compute derived required documents for the applicant.
 *
 * Reads selections from applicationData and returns a list of sections
 * (each with a groupKey + list of DocumentSpecs) in the order the UI
 * should render them: Standard → Income → Asset.
 * ────────────────────────────────────────────────────────────────────────── */

// `viewer.isHoh = false` is the co-applicant slice flow: each adult has their
// own Application + S3 bucket, so they only see slots for themselves.
// Photo ID is ALWAYS self-only: every adult (incl. co-applicants) uploads their
// own government ID in their own sub-application, so the HoH is never required
// to upload a co-applicant's ID. The HoH view still fans out ssCard across every
// member (incl. minors) and birthCert across every minor.
export const getRequiredSections = (
  applicationData: ApplicationData,
  viewer: { isHoh: boolean } = { isHoh: true }
): DocumentSection[] => {
  const sections: DocumentSection[] = [];

  const memberProfiles = applicationData.memberProfiles as
    | MemberProfilesLike
    | undefined;
  const members = memberProfiles?.members || [];

  const memberName = (m: MemberLike | MemberProfilesLike): string => {
    const parts = [m.firstName, m.lastName].filter(Boolean);
    return parts.length > 0 ? parts.join(" ") : "";
  };

  const primaryName = memberProfiles ? memberName(memberProfiles) : "";

  const minorMembers = members.filter(
    (m) => m.relationship === "Family Member - Minor"
  );
  const minorSubjects = minorMembers.map((m) => ({
    id: `member-${members.indexOf(m)}`,
    name: memberName(m),
  }));

  // Student Status (Step 2) — Option B or C = at least one full-time / part-time student
  const studentDetails = applicationData.studentDetails as
    | StudentDetailsLike
    | undefined;
  const hasStudent =
    studentDetails?.householdStatus === "B" ||
    studentDetails?.householdStatus === "C";

  // Spousal Status (Step 4 / HouseholdIncome) — divorce/separation papers
  // are required when the resident said they possess legal documents to
  // verify divorce / separation / etc. (cert form question `docs_yes`).
  const income = (applicationData.householdIncome as HouseholdIncomeForm) || {};
  const needsDivorceSeparationPapers = income.possessLegalDocs === true;

  /* ── Standard section ────────────────────────────────────────────────── */
  const standardDocs: DocumentSpec[] = [];

  // Viewer-scoped subject lists. HoH sees the full household fanout; a
  // co-applicant slice sees only their own self-slot (photoId + ssCard) and
  // no birth certificates (minors are entirely on the HoH's record).
  const selfSubject = { id: "primary", name: primaryName };
  // Photo ID is self-only in every view: co-applicants upload their own ID in
  // their own sub-application, so the HoH is never required to upload theirs.
  const photoIdSubjects = [selfSubject];
  // SS Card follows the same rule as photo ID for adults — each adult uploads
  // their own in their sub-application, so the HoH is never asked for another
  // adult's card. Minors have no sub-application, so the HoH still provides
  // theirs (same as birth certificates below).
  const ssCardSubjects = viewer.isHoh
    ? [selfSubject, ...minorSubjects]
    : [selfSubject];
  const birthCertSubjects = viewer.isHoh ? minorSubjects : [];

  // Photo ID — self only (each adult uploads their own in their sub-application)
  photoIdSubjects.forEach((s) => {
    standardDocs.push({
      key: `photoId:${s.id}`,
      baseKey: "photoId",
      subject: s.name,
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    });
  });

  // SS Card — self + minors (HoH view) or just self (co-applicant slice)
  ssCardSubjects.forEach((s) => {
    standardDocs.push({
      key: `ssCard:${s.id}`,
      baseKey: "ssCard",
      subject: s.name,
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    });
  });

  // Birth Certificate — one slot per minor (HoH view); never shown in slice
  birthCertSubjects.forEach((s) => {
    standardDocs.push({
      key: `birthCertificate:${s.id}`,
      baseKey: "birthCertificate",
      subject: s.name,
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    });
  });

  // Custody Court Order — only when the applicant said they don't have full
  // legal custody AND a court order exists for that minor. HUD/PBRA/811
  // verification flow; HoH view only (co-applicants don't manage minors).
  if (viewer.isHoh) {
    minorMembers.forEach((m) => {
      const memberLike = m as MemberLike & {
        hasFullLegalCustody?: string;
        hasCourtOrder?: string;
      };
      if (
        memberLike.hasFullLegalCustody === "no" &&
        memberLike.hasCourtOrder === "yes"
      ) {
        const id = `member-${members.indexOf(m)}`;
        standardDocs.push({
          key: `custodyOrder:${id}`,
          baseKey: "custodyOrder",
          subject: memberName(m),
          count: 1,
          accept: ACCEPT,
          maxSizeMb: MAX_MB,
        });
      }
    });
  }

  if (hasStudent) {
    standardDocs.push({
      key: "studentStatusProof",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    });
  }
  if (needsDivorceSeparationPapers) {
    standardDocs.push({
      key: "divorceSeparationPapers",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    });
  }
  // Offer Letter — required when the applicant has accepted an offer of
  // employment but hasn't started yet. Drives the Non-Employed cert's
  // Section B (`has_job_offer_not_started`). Only emit on HoH/self view —
  // co-applicant slice naturally emits this for themselves since the
  // signal is on their own application's householdIncome.
  if (income.pendingJobOffer === true) {
    standardDocs.push({
      key: "offerLetter",
      count: 1,
      accept: ACCEPT,
      maxSizeMb: MAX_MB,
    });
  }
  sections.push({
    sectionKey: "standard",
    groupKey: "standard",
    documents: standardDocs,
  });

  /* ── Income sections ─────────────────────────────────────────────────── */
  (Object.keys(INCOME_DOCS) as IncomeKey[]).forEach((key) => {
    if (income[key] === true) {
      const docs = INCOME_DOCS[key];
      if (docs) {
        sections.push({
          sectionKey: "income",
          groupKey: key,
          documents: docs,
        });
      }
    }
  });

  // Child Support attachments — status-specific per the cert form text.
  // (a)/(b) -> OAG, (c) -> cash contribution verification, (d) -> attorney
  // letter, (e) -> nothing.
  const csDocs: DocumentSpec[] = [];
  if (income.childSupportStatus === "a" || income.childSupportStatus === "b") {
    csDocs.push(CHILD_SUPPORT_OAG_DOC);
  } else if (income.childSupportStatus === "c") {
    csDocs.push(CHILD_SUPPORT_CASH_DOC);
  } else if (income.childSupportStatus === "d") {
    csDocs.push(CHILD_SUPPORT_ATTORNEY_DOC);
  }
  if (csDocs.length) {
    sections.push({
      sectionKey: "income",
      groupKey: "childSupport",
      documents: csDocs,
    });
  }
  // Alimony documents — required whenever the resident has been awarded
  // income such as alimony / child support / survivor benefits (only asked
  // for Sep/Estranged residents).
  if (income.awardedIncome === true) {
    sections.push({
      sectionKey: "income",
      groupKey: "alimonySpousalSupport",
      documents: ALIMONY_DOCS,
    });
  }

  /* ── Asset sections ──────────────────────────────────────────────────── */
  // HOTMA threshold gate: households whose self-declared net assets are at or
  // below the imputation threshold ($52,787, CY2026) self-certify and upload
  // NO asset documents (SME-confirmed). Only households OVER the threshold get
  // asset-verification upload requirements. We compute the total from the
  // per-asset cash values captured on the Assets step; if no values were
  // captured (legacy in-flight applications), we fall back to showing the docs
  // so verification is never silently skipped.
  const assets = (applicationData.assets as AssetsForm) || {};
  const assetValues = (assets.assetValues || {}) as Record<
    string,
    { cashValue?: string } | undefined
  >;
  let grossAssetTotal = 0;
  let anyValueCaptured = false;
  for (const [key, detail] of Object.entries(assetValues)) {
    if ((assets as unknown as Record<string, unknown>)[key] !== true) continue;
    const raw = (detail?.cashValue ?? "").trim();
    if (raw) {
      anyValueCaptured = true;
      const n = parseFloat(raw);
      if (Number.isFinite(n)) grossAssetTotal += n;
    }
  }
  // HOTMA: exclude a still-held federal refund from net family assets (24 CFR
  // 5.603). Must match FinancialSummary.tsx so the displayed total and this
  // upload-requirement gate key off the same countable figure.
  const heldRefund =
    assets.federalRefundReceived === true
      ? parseFloat((assets.federalRefundStillHeld ?? "").trim()) || 0
      : 0;
  const assetTotal = Math.max(0, grossAssetTotal - heldRefund);
  const underThreshold =
    anyValueCaptured && assetTotal <= configs.HOTMA_ASSET_THRESHOLD;

  if (!underThreshold) {
    (Object.keys(ASSET_DOCS) as (keyof AssetsForm)[]).forEach((key) => {
      if (assets[key] === true) {
        const docs = ASSET_DOCS[key];
        if (docs) {
          sections.push({
            sectionKey: "asset",
            groupKey: String(key),
            documents: docs,
          });
        }
      }
    });
  }

  /* ── Live-In Aide section ───────────────────────────────────────────────── */
  // Triggered when any household member has a relationship matching the
  // Live-In Aide pattern. One upload is required:
  //   LIA Need Verification — medical/disability need documentation, completed
  //   offline by the applicant's physician.
  // The LIA Questionnaire is collected as an e-signed DocuSeal form (not an
  // upload) and the LIA Self-Certification is likewise e-signed, so neither
  // appears here.
  const hasLiveInAide = members.some((m) =>
    /live.?in.?aide/i.test(m.relationship ?? "")
  );
  if (hasLiveInAide) {
    const LIA_DOCS: DocumentSpec[] = [
      {
        key: "liaNeedVerification",
        count: 1,
        accept: ACCEPT,
        maxSizeMb: MAX_MB,
        hasTemplate: true,
        templateUrl: "/forms/live-in-aide-need-verification.pdf",
      },
    ];
    sections.push({
      sectionKey: "standard",
      groupKey: "liveInAide",
      documents: LIA_DOCS,
    });
  }

  return sections;
};
